import { Icon, Typography } from '@globalmed/atlas-ui';
import { type DragEvent, memo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formBuilderElements } from '../constants';
import { useFormBuilderFields } from '../hooks';
import styles from './FormBuilderListElements.module.css';
import { EFormBuilderFieldType } from '@/types';
import { isValueOfEnum } from '@/utils/isValueOfEnum';

type TProps = {
  includeElements?: EFormBuilderFieldType[];
};

export const FormBuilderListElements =
  /**
   * List of draggable form builder elements that can be added to the form
   * @param {TProps} props - Component props
   * @param {EFormBuilderFieldType[]} [props.includeElements] - Optional array of element types to include, shows all if not specified
   * @returns {JSX.Element} Form builder elements list component
   */
  memo(({ includeElements }: TProps) => {
    const { t } = useTranslation();
    const { addField } = useFormBuilderFields();
    const headingId = useId();

    const handleDragStart = (e: DragEvent<HTMLButtonElement>, key: string) => {
      e.dataTransfer.setData('text/plain', key);
    };
    const handleClick = (key: string) => {
      if (!isValueOfEnum(key, EFormBuilderFieldType))
        throw new Error('Bad key');
      addField(key);
    };

    const formBuilderElementsEntries = Object.entries(formBuilderElements);

    const elements = includeElements
      ? formBuilderElementsEntries.filter(([key]) =>
          includeElements.some((el) => el === key),
        )
      : formBuilderElementsEntries;

    return (
      <div className={styles.container}>
        <Typography id={headingId} color="var(--colors-gray600)">
          {t('form-builder.form-elements')}
        </Typography>

        <ul className={styles.list} aria-labelledby={headingId}>
          {elements.map(([key, values]) => (
            <li key={key} className={styles.item}>
              <button
                className={styles.element}
                type="button"
                draggable
                onDragStart={(e) => handleDragStart(e, key)}
                onClick={() => handleClick(key)}
              >
                <Icon size="16px" name={values.icon} decorative />
                <Typography
                  as="span"
                  fontWeight={500}
                  color="var(--colors-gray600)"
                >
                  {t(values.value)}
                </Typography>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  });

FormBuilderListElements.displayName = 'FormBuilderListElements';
